import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAdminApi } from '../../hooks/useAdminApi';
import AdminLayout from '../../components/admin/AdminLayout';
import RenewLicenseModal from '../../components/admin/RenewLicenseModal';
import GenerateLicenseModal from '../../components/admin/GenerateLicenseModal';
import {
  Search,
  Filter,
  Key,
  RefreshCw,
  Eye,
  RotateCcw,
  Copy,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Building2,
  Calendar,
  Ban
} from 'lucide-react';

const LicensesListPage = () => {
  const { get, post, loading } = useAdminApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [licenses, setLicenses] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [planFilter, setPlanFilter] = useState(searchParams.get('plan') || '');
  const [refreshing, setRefreshing] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [copied, setCopied] = useState(null);
  const dropdownButtonRefs = useRef({});

  // Modals
  const [renewModal, setRenewModal] = useState({ open: false, license: null });
  const [generateModal, setGenerateModal] = useState({ open: false, tenant: null });

  useEffect(() => {
    fetchLicenses();
  }, [pagination.page, statusFilter, planFilter]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (pagination.page === 1) {
        fetchLicenses();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [search]);

  const fetchLicenses = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (planFilter) params.append('plan', planFilter);

      // Check for tenant filter from URL
      const tenantId = searchParams.get('tenant');
      if (tenantId) params.append('tenant', tenantId);

      const response = await get(`/licenses?${params.toString()}`);
      if (response.success) {
        setLicenses(response.data || response.licenses || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 1
        }));
      }
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRevoke = async (license) => {
    const reason = window.prompt('Enter reason for revoking this license:');
    if (!reason) return;

    try {
      // Backend expects tenantId in body, not licenseId in URL
      const tenantId = license.tenant?.tenantId || license.tenantId;
      const response = await post('/licenses/revoke', {
        tenantId,
        reason
      });
      if (response.success) {
        fetchLicenses();
      }
    } catch (error) {
      console.error('Failed to revoke license:', error);
      alert('Failed to revoke license: ' + (error.message || 'Unknown error'));
    }
    setDropdownOpen(null);
  };

  const handleDropdownToggle = (licenseId) => {
    if (dropdownOpen === licenseId) {
      setDropdownOpen(null);
    } else {
      const buttonEl = dropdownButtonRefs.current[licenseId];
      if (buttonEl) {
        const rect = buttonEl.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.right - 192 + window.scrollX // 192px = w-48 dropdown width
        });
      }
      setDropdownOpen(licenseId);
    }
  };

  const handleCopy = (licenseKey, licenseId) => {
    navigator.clipboard.writeText(licenseKey);
    setCopied(licenseId);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleModalClose = (refreshData = false) => {
    setRenewModal({ open: false, license: null });
    setGenerateModal({ open: false, tenant: null });
    if (refreshData) {
      fetchLicenses();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'expiring_soon':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'revoked':
        return <Ban className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status, daysRemaining) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      expired: 'bg-red-100 text-red-700',
      expiring_soon: 'bg-yellow-100 text-yellow-700',
      revoked: 'bg-gray-100 text-gray-700'
    };

    let displayStatus = status;
    if (status === 'active' && daysRemaining <= 30) {
      displayStatus = 'expiring_soon';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[displayStatus] || styles.active}`}>
        {getStatusIcon(displayStatus)}
        <span className="ml-1.5">
          {displayStatus === 'expiring_soon' ? `${daysRemaining} days left` : displayStatus?.replace('_', ' ').charAt(0).toUpperCase() + displayStatus?.replace('_', ' ').slice(1)}
        </span>
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const styles = {
      starter: 'bg-blue-100 text-blue-700',
      professional: 'bg-purple-100 text-purple-700',
      enterprise: 'bg-pink-100 text-pink-700'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[plan] || styles.starter}`}>
        {plan?.charAt(0).toUpperCase() + plan?.slice(1)}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateDaysRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Licenses</h1>
            <p className="text-gray-500 mt-1">Manage all tenant licenses</p>
          </div>
          <button
            onClick={fetchLicenses}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by license key or tenant..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="expiring_soon">Expiring Soon</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
              <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Plan Filter */}
            <div className="relative">
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Plans</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Licenses Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    License Key
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && licenses.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Loading licenses...</p>
                    </td>
                  </tr>
                ) : licenses.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">No licenses found</p>
                      <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                ) : (
                  licenses.map((license) => {
                    const daysRemaining = calculateDaysRemaining(license.expiresAt);
                    return (
                      <tr key={license._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Key className="w-5 h-5 text-white" />
                            </div>
                            <div className="ml-3">
                              <p className="font-mono text-sm text-gray-900">
                                {license.licenseKey?.substring(0, 20)}...
                              </p>
                              <button
                                onClick={() => handleCopy(license.licenseKey, license._id)}
                                className="text-xs text-purple-600 hover:text-purple-700 flex items-center mt-1"
                              >
                                {copied === license._id ? (
                                  <>
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3 mr-1" />
                                    Copy
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Link
                            to={`/admin/tenants/${license.tenant?._id}`}
                            className="flex items-center text-gray-900 hover:text-purple-600"
                          >
                            <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                            {license.tenant?.companyName || 'Unknown'}
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          {getPlanBadge(license.plan)}
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(license.status, daysRemaining)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-gray-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            {formatDate(license.expiresAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          {formatDate(license.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="relative inline-block">
                            <button
                              ref={(el) => dropdownButtonRefs.current[license._id] = el}
                              onClick={() => handleDropdownToggle(license._id)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-gray-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} licenses
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="px-4 py-2 text-sm font-medium text-gray-700">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Position Dropdown */}
      {dropdownOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setDropdownOpen(null)}
          />
          <div
            className="fixed w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left
            }}
          >
            {(() => {
              const license = licenses.find(l => l._id === dropdownOpen);
              if (!license) return null;
              return (
                <>
                  <Link
                    to={`/admin/tenants/${license.tenant?._id}`}
                    onClick={() => setDropdownOpen(null)}
                    className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50"
                  >
                    <Eye className="w-4 h-4 mr-3" />
                    View Tenant
                  </Link>
                  {license.status === 'active' && (
                    <button
                      onClick={() => {
                        setRenewModal({ open: true, license });
                        setDropdownOpen(null);
                      }}
                      className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50"
                    >
                      <RotateCcw className="w-4 h-4 mr-3" />
                      Renew License
                    </button>
                  )}
                  {license.status === 'active' && (
                    <button
                      onClick={() => handleRevoke(license)}
                      className="w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50"
                    >
                      <Ban className="w-4 h-4 mr-3" />
                      Revoke License
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* Modals */}
      {renewModal.open && (
        <RenewLicenseModal
          license={renewModal.license}
          onClose={() => handleModalClose(false)}
          onSave={() => handleModalClose(true)}
        />
      )}
    </AdminLayout>
  );
};

export default LicensesListPage;
